import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { SidebarService } from '../../services/sidebar.service';
import { IUser } from '../../interfaces/user.interface';
import { isSuperAdmin } from '../../guards/super-admin.guard';
import { ClubRequestsService } from '../../pages/club-requests/club-requests.service';
import { AccountRequestsService } from '../../pages/account-requests/account-requests.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  // De breedte van de balk zit in de balk zelf, niet in een wikkel eromheen.
  // Zo hoeft alleen deze component te weten hoe smal 'ingeklapt' is.
  host: { '[class.ingeklapt]': 'ingeklapt' }
})
export class SidebarComponent implements OnInit {

  public user: IUser;
  public superAdmin = false;

  private readonly clubRequests = inject(ClubRequestsService);
  private readonly destroyRef = inject(DestroyRef);
  /** Openstaande clubaanvragen: het getal naast "Club requests". */
  public readonly aanvragen = this.clubRequests.pendingCount;
  private readonly accountRequests = inject(AccountRequestsService);
  /** Openstaande aanvragen vanaf het inlogscherm: het getal naast "Account requests". */
  public readonly accountAanvragen = this.accountRequests.pendingCount;

  constructor(private router: Router,
              private authService: AuthService,
              private sidebarService: SidebarService) {}

  /** Ingeklapt: alleen de pictogrammen, zonder de teksten ernaast. */
  public get ingeklapt(): boolean {
    return this.sidebarService.ingeklapt;
  }

  public wisselZijbalk(): void {
    this.sidebarService.wissel();
  }

  // ---- Schermvullend ------------------------------------------------------
  //
  // Chrome's adresbalk, tabbladen en bladwijzerbalk kosten samen zo'n 120
  // pixels bovenaan. Op een scherm waar je een wedstrijd lang naar kijkt is
  // dat een hele tabelrij (gevraagd 02-09-2026).

  /**
   * Staat de console nu schermvullend?
   *
   * LET OP -- dit weet alleen van schermvullend dat via DEZE knop is aangezet.
   * De F11 van Chrome is browser-fullscreen: die laat `fullscreenElement` leeg
   * en stuurt geen `fullscreenchange`, dus dan blijft het pictogram op
   * "aanzetten" staan terwijl je al schermvullend zit. Dat is een grens van de
   * browser en niet iets wat hier op te lossen valt; onschadelijk ook, want de
   * knop doet het daar gewoon.
   */
  public schermvullend = false;

  // Ook luisteren naar wat er BUITEN de knop om gebeurt: Esc, of het menu van
  // de browser. Zonder dit wijst het pictogram de verkeerde kant op zodra
  // iemand op een andere manier terugkeert.
  @HostListener('document:fullscreenchange')
  public opSchermvullendGewisseld(): void {
    this.schermvullend = !!document.fullscreenElement;
  }

  public wisselSchermvullend(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
      return;
    }
    // Een browser mag dit weigeren (instelling, of een venster dat het niet
    // toestaat). Dan gebeurt er niets -- beter dan een foutmelding over iets
    // waar de gebruiker toch niets aan kan veranderen.
    document.documentElement.requestFullscreen?.().catch(() => undefined);
  }

  ngOnInit(): void {
    this.user = this.authService.user;
    this.superAdmin = isSuperAdmin(this.user);
    if (this.superAdmin) {
      // Bij elke navigatie bijwerken; goedkeuren/afkeuren werkt hem zelf bij.
      this.clubRequests.refreshCount();
      this.accountRequests.refreshCount();
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => {
        this.clubRequests.refreshCount();
        this.accountRequests.refreshCount();
      });
    }
  }

  /**
   * De initialen voor het rondje bij het account.
   *
   * Hier stond een foto van picsum.photos - een dienst die bij elke pagina
   * een WILLEKEURIGE foto van een externe server haalde. Dat is geen
   * portret van de gebruiker, het maakt de beheerconsole afhankelijk van een
   * site waar wij niets over te zeggen hebben, en het meldt bij elke
   * paginaweergave aan die site dat er iemand inlogt.
   */
  public get initialen(): string {
    const voor = (this.user?.firstName || '').trim();
    const achter = (this.user?.lastName || '').trim();
    return `${voor.charAt(0)}${achter.charAt(0)}`.toUpperCase() || '?';
  }

  public onLogOut(): void {
    this.authService.logout();
    this.router.navigateByUrl('/login');
  }

  /** `deel`: ook actief op een pad daaronder (bv. de paneeleditor onder /panels/club). */
  public isRouteActive(route: string, deel = false): boolean {
    const matchOptions: IsActiveMatchOptions = {
      paths: deel ? 'subset' : 'exact',
      queryParams: 'ignored',  // Can be 'exact', 'subset', or 'ignored'
      matrixParams: 'ignored', // Can be 'exact', 'subset', or 'ignored'
      fragment: 'ignored'      // Can be 'exact' or 'ignored'
    };

    return this.router.isActive(route, matchOptions);
  }
}
